/** Sort and review order: most urgent first. The filter tabs use a different order (see QUEUE_FILTERS). */
const QUEUE_STATUS_ORDER = ['needs_input', 'ready_to_review', 'preparing', 'pending', 'couldnt_apply', 'sent'];

/** The statuses an application can be reviewed in. */
const REVIEWABLE_STATUSES = ['needs_input', 'ready_to_review'];

const STATUS_LABELS = {
	needs_input: 'Needs your input',
	ready_to_review: 'Ready to review',
	preparing: 'Preparing',
	pending: 'Pending',
	sent: 'Sent',
	couldnt_apply: "Couldn't apply",
};

/** Filter tabs in display order: Sent comes before Couldn't apply, unlike QUEUE_STATUS_ORDER. */
const QUEUE_FILTERS = [
	{ value: 'all', label: 'All' },
	...['needs_input', 'ready_to_review', 'preparing', 'pending', 'sent', 'couldnt_apply'].map((value) => ({
		value,
		label: STATUS_LABELS[value],
	})),
];

/** Every action's label except `remove`, which is an icon-only button. */
const ACTION_LABELS = {
	review: 'Review',
	cancel_send: 'Cancel Send',
	apply_externally: 'Apply Externally',
	resubmit: 'Review & Resubmit',
	view_details: 'View Details',
	open_in_tracker: 'Open in JobTrackerAI',
	mark_applied: 'Mark as Applied',
	report_issue: 'Report Issue',
	delete: 'Delete from queue',
};

/** The bottom sheet's labels: ACTION_LABELS, except that Review reads "Review Application" there. */
const SHEET_ACTION_LABELS = { ...ACTION_LABELS, review: 'Review Application' };

/** The label of a queued Saved job's button, by queuedJobAction() (EN-1303). */
const QUEUED_JOB_ACTION_LABELS = { review: 'Review Application', queue: 'View in Queue' };

/** The label of the link in a Saved card's queue status footer, by savedQueueFooter() (EN-1303). */
const SAVED_FOOTER_LINK_LABELS = { review: 'Review', queue: 'Open' };

/** The steps a preparing application goes through, in order (AutoApplyPreparationStep). */
const PREPARATION_STEP_ORDER = ['tailoring_resume', 'writing_cover_letter', 'answering_questions'];

const PREPARING_TOOLTIPS = {
	tailoring_resume: 'Tailoring your resume',
	writing_cover_letter: 'Writing your cover letter',
	answering_questions: 'Answering application questions',
};

/** The server's flag, on a preparing application's `preparation`, for each document being finished. */
const DONE_FLAGS = {
	resume: 'resume_done',
	cover_letter: 'cover_letter_done',
};

// Placeholder copy (D1) until Denis answers EN-1235's "Open for Denis".
const EXTERNAL_APPLY_TOOLTIP =
	'This company’s form can’t be sent automatically. Apply on their site with your prepared answers.';

// Placeholder copy (D2) until Denis answers EN-1235's "Open for Denis".
const RESUME_MISMATCH_TOOLTIP = 'Your resume isn’t a close match for this job';

// Placeholder copy until Denis answers on EN-1321.
const SENDING_LABEL = 'Sending';
const SENDING_TOOLTIP = 'Being sent now. It can no longer be cancelled.';

const rank = (application) => QUEUE_STATUS_ORDER.indexOf(application.status);

/**
 * Orders applications by status, then the most recently updated first. Returns a new array.
 */
const sortForQueue = (applications) =>
	[...applications].sort((a, b) => rank(a) - rank(b) || Date.parse(b.updated_at) - Date.parse(a.updated_at));

/**
 * The applications to review, in the order the pager walks them: Needs your input first, then Ready to review,
 * each newest first. Returns a new array.
 */
const reviewOrder = (applications) =>
	sortForQueue(applications).filter(({ status }) => REVIEWABLE_STATUSES.includes(status));

/**
 * Whether the review screen shows its guidance banner: until the seeker has submitted an AutoApply, and whenever
 * Complete AutoApply is on.
 */
const shouldShowReviewBanner = ({ hasSubmittedAutoApply, isCompleteAutoApplyOn }) =>
	!hasSubmittedAutoApply || isCompleteAutoApplyOn;

/**
 * Compact "time since" label for the Updated column, e.g. "8m ago".
 */
const formatUpdatedAgo = (timestamp, now) => {
	if (!timestamp) {
		return '';
	}

	const seconds = Math.floor((now - Date.parse(timestamp)) / 1000);

	if (seconds < 60) {
		return 'Just now';
	}

	if (seconds < 3600) {
		return `${Math.floor(seconds / 60)}m ago`;
	}

	if (seconds < 86400) {
		return `${Math.floor(seconds / 3600)}h ago`;
	}

	return `${Math.floor(seconds / 86400)}d ago`;
};

/**
 * "Sent 2h ago" for the View Details top bar, from the same clock as the Updated column.
 */
const formatSentAgo = (timestamp, now) => {
	const ago = formatUpdatedAgo(timestamp, now);

	if (!ago) {
		return 'Sent';
	}

	return ago === 'Just now' ? 'Sent just now' : `Sent ${ago}`;
};

const jobSubtitle = ({ company, location }) => [company, location].filter(Boolean).join(' • ');

/**
 * The queue's copy of an application's job, in the shape the job panel reads. The review and View Details show it
 * while the job loads, and keep it when the job cannot be loaded.
 */
const queuedJobSnapshot = (queuedJob) => ({
	provider_job_id: queuedJob.id,
	job_title: queuedJob.title,
	company: queuedJob.company,
	location: queuedJob.location,
	apply_url: queuedJob.url,
});

/**
 * "{Company} • {Location} • {posted time}" for the mobile job card and its Job Description sheet (EN-1255), from a
 * job in the job panel's shape. A queued snapshot has no posted time until the job loads, so what is missing is left
 * out rather than leaving a stray bullet.
 */
const jobCardMeta = ({ company, location, timeAgoPosted }) => [company, location, timeAgoPosted]
	.filter(Boolean)
	.join(' • ');

const isResumeMismatch = (application) => application.needs_input?.reason === 'resume_mismatch';

/** Whether the application is in Couldn't apply because its job is gone. */
const isJobExpired = (application) => application.couldnt_apply?.reason === 'job_expired';

/**
 * Whether the application's job is gone (EN-1299): archived, deleted or found expired. An application can still wait
 * for review with its job gone, until the job's expiry moves it to Couldn't apply.
 */
const isJobUnavailable = (application) => application.job?.is_available === false || isJobExpired(application);

/**
 * Whether a Couldn't apply application can be finished on the company's site: every reason except Job expired
 * (EN-1248).
 */
const canApplyExternally = (application) => application.status === 'couldnt_apply' && !isJobExpired(application);

/**
 * Whether a Pending application is already being sent: a worker has claimed it, so Cancel Send can no longer stop it
 * (EN-1321). The queue counts and filters it as Pending all the same.
 */
const isSending = (application) => application.status === 'pending' && application.pending?.is_sending === true;

/** The status badge's label: STATUS_LABELS, except that a Pending application already being sent reads "Sending". */
const statusLabel = (application) => (isSending(application) ? SENDING_LABEL : STATUS_LABELS[application.status]);

const statusTooltip = (application) => {
	switch (application.status) {
		case 'preparing':
			return PREPARING_TOOLTIPS[application.preparation?.step] ?? '';
		case 'needs_input': {
			if (isResumeMismatch(application)) {
				return RESUME_MISMATCH_TOOLTIP;
			}

			const { count } = application.needs_input;

			return `${count} company-specific ${count === 1 ? 'question' : 'questions'}`;
		}
		case 'ready_to_review':
			return 'Resume, cover letter and answers are filled in';
		case 'pending':
			return isSending(application) ? SENDING_TOOLTIP : 'Will be sent soon';
		case 'sent':
			return 'Track it in JobTrackerAI';
		case 'couldnt_apply': {
			const { reason, is_validation_failure: isValidationFailure } = application.couldnt_apply;

			if (reason === 'job_expired') {
				return 'This job is no longer accepting applications.';
			}

			if (reason === 'external_apply') {
				return EXTERNAL_APPLY_TOOLTIP;
			}

			return isValidationFailure
				? 'Some answers are missing or invalid. Review and resubmit.'
				: 'We couldn’t complete the company form';
		}
		default:
			return '';
	}
};

/**
 * Whether a document cell shows a spinner ('generating'), its finished label ('done') or a dash ('none').
 */
const documentState = (application, document) => {
	if (isResumeMismatch(application)) {
		return 'none';
	}

	if (application.status === 'preparing') {
		return application.preparation?.[DONE_FLAGS[document]] === true ? 'done' : 'generating';
	}

	return 'done';
};

/**
 * What a row offers: the row click, the primary button, the trash button and the "More actions" menu entries.
 */
const rowActions = (application) => {
	switch (application.status) {
		case 'needs_input':
		case 'ready_to_review':
			return { click: 'review', primary: 'review', canRemove: true, menu: [] };
		case 'preparing':
			return { click: null, primary: null, canRemove: true, menu: [] };
		case 'pending':
			return { click: null, primary: isSending(application) ? null : 'cancel_send', canRemove: false, menu: [] };
		case 'sent':
			// View Details needs something to show, which a send from before EN-1143 lacks, and Open in JobTrackerAI a
			// tracker card, which the user may have deleted.
			return {
				click: application.has_details ? 'view_details' : null,
				primary: null,
				canRemove: false,
				menu: [
					application.has_details && 'view_details',
					application.job_tracker_application_uuid && 'open_in_tracker',
				].filter(Boolean),
			};
		case 'couldnt_apply': {
			const { reason, is_validation_failure: isValidationFailure } = application.couldnt_apply;

			if (reason === 'job_expired') {
				return { click: null, primary: null, canRemove: false, menu: ['report_issue', 'delete'] };
			}

			const menu = ['mark_applied', 'report_issue', 'delete'];

			return isValidationFailure
				? { click: null, primary: 'resubmit', canRemove: false, menu: ['apply_externally', ...menu] }
				: { click: null, primary: 'apply_externally', canRemove: false, menu };
		}
		default:
			return { click: null, primary: null, canRemove: false, menu: [] };
	}
};

/**
 * A card's tap and its one button, and the bottom sheet's rows in order: Review, the More actions entries, Delete.
 */
const cardActions = (application) => {
	const { click, primary, canRemove, menu } = rowActions(application);

	return {
		click,
		primary,
		sheet: [primary === 'review' && 'review', ...menu, canRemove && 'delete'].filter(Boolean),
	};
};

/** A Saved card's queue status footer, `{ text, link }`, or null for no application or a sent one (EN-1303). */
const savedQueueFooter = (application) => {
	switch (application?.status) {
		case 'preparing':
			return { text: 'In your AutoApply queue', link: null };
		case 'ready_to_review':
			return { text: 'Ready to submit', link: 'review' };
		case 'needs_input': {
			const count = application.needs_input?.count;

			if (isResumeMismatch(application) || !count) {
				return { text: null, link: 'review' };
			}

			return { text: `${count} ${count === 1 ? 'Question' : 'Questions'}`, link: 'review' };
		}
		case 'pending':
			// The badge beside it reads "Sending" once a worker has the application (EN-1321).
			return { text: isSending(application) ? null : 'Sending soon', link: null };
		case 'couldnt_apply':
			return { text: 'See your queue', link: 'queue' };
		default:
			return null;
	}
};

/** The button a queued Saved job shows: 'review' or 'queue', or null for no application or a sent one (EN-1303). */
const queuedJobAction = (application) => {
	const status = application?.status;

	if (REVIEWABLE_STATUSES.includes(status)) {
		return 'review';
	}

	return ['preparing', 'pending', 'couldnt_apply'].includes(status) ? 'queue' : null;
};

/** Where a queued Saved job's link goes: its review, else the queue tab (EN-1303). */
const queueRoute = (application, action) =>
	action === 'review' && application?.uuid
		? { name: 'job-board-autoapply', query: { review: application.uuid } }
		: { name: 'job-board-autoapply' };

export {
	ACTION_LABELS,
	PREPARATION_STEP_ORDER,
	QUEUE_FILTERS,
	QUEUE_STATUS_ORDER,
	QUEUED_JOB_ACTION_LABELS,
	REVIEWABLE_STATUSES,
	SAVED_FOOTER_LINK_LABELS,
	SHEET_ACTION_LABELS,
	STATUS_LABELS,
	canApplyExternally,
	cardActions,
	documentState,
	formatSentAgo,
	formatUpdatedAgo,
	isJobExpired,
	isJobUnavailable,
	isResumeMismatch,
	isSending,
	jobCardMeta,
	jobSubtitle,
	queueRoute,
	queuedJobAction,
	queuedJobSnapshot,
	reviewOrder,
	rowActions,
	savedQueueFooter,
	shouldShowReviewBanner,
	sortForQueue,
	statusLabel,
	statusTooltip,
};
